// Field permissions configuration based on roles
// Based on the provided spreadsheet mapping

export const fieldPermissions = {
  // Membership Information
  membershipInformation: {
    RepAssigned1: ['John Miller', 'Admin', 'Manager', 'Maintainer'],
    ServiceFieldRep1: ['John Miller', 'Admin', 'Manager', 'Maintainer'],
    FieldRep1: ['John Miller', 'Admin', 'Manager', 'Maintainer'],
    FieldRep2: ['John Miller', 'Admin', 'Manager', 'Maintainer'],
    Organizer: ['Kelly Martinez', 'Alex Guzman', 'Admin', 'Manager', 'Maintainer'],
    RepAssigned2: ['John Miller', 'Admin', 'Manager', 'Maintainer'],
    CBA_START: ['Assigned Staff Rep', 'Admin', 'Manager', 'Maintainer'],
    CBA_EXP: ['Assigned Staff Rep', 'Admin', 'Manager', 'Maintainer'],
    TOWN: ['Admin', 'Manager', 'Maintainer'],
    STATE_REP_MUNI_BOE: ['Admin', 'Manager', 'Maintainer'],
    HOUSE_REP_MUNI_BOE: ['Admin', 'Manager', 'Maintainer'],
    CONGRESSIONAL_DISTRICT_MUNI_BOE: ['Admin', 'Manager', 'Maintainer'],
  },

  // Payroll and HR Contacts
  payrollHRContacts: {
    PayrollHRContactName: ['Assigned Staff Rep', 'Membership', 'Admin'],
    PayrollHRContactEmail: ['Assigned Staff Rep', 'Membership', 'Admin'],
    PayrollHRContactNumber: ['Assigned Staff Rep', 'Membership', 'Admin'],
    PayrollHRContactTitle: ['Assigned Staff Rep', 'Membership', 'Admin'],
    PayrollHRContactType: ['Assigned Staff Rep', 'Membership', 'Admin'],
  },

  // Contract and Negotiations
  contractNegotiations: {
    NewRecommendedContractLanguage: ['Assigned Staff Rep', 'Admin'],
    AreOrientationsHappening: ['Assigned Staff Rep', 'Admin'],
    WhoDoesOrientation: ['Assigned Staff Rep', 'Admin'],
  },

  // Membership Metrics
  membershipMetrics: {
    ActiveJobCount: ['Membership', 'Admin'],
    TotalDuesPayers: ['Membership', 'Admin'],
    OnCentralizedDues: ['Membership', 'Admin'],
    DuesMonthApplied: ['Membership', 'Admin'],
    DuesFrequency: ['Membership', 'Admin'],
    NumberOfHomeEmails: ['Membership', 'Admin'],
    NumberOfPhoneNumbers: ['Membership', 'Admin'],
  },

  // Audits and Compliance
  auditsCompliance: {
    DateLastBargUnitListReceived: ['Membership', 'Admin'],
    Refusals: ['Membership', 'Admin'],
    AuditCompleteOverview: ['Membership', 'Admin'],
    NoCardNoDues: ['Membership', 'Admin'],
    DuesNoCard: ['Membership', 'Admin'],
    TotalOPTOUTS: ['Membership', 'Admin'],
    CardNoDues: ['Membership', 'Admin'],
    DuesWithCard: ['Membership', 'Admin'],
  },

  // Financial Metrics
  financialMetrics: {
    ein: ['Assigned Staff Rep', 'Membership', 'Admin'],
    PEOPLEContributors: ['Membership', 'Admin'],
    RecommitsSaves: ['Membership', 'Admin'],
  },

  // Election and Officer Information
  electionOfficerInfo: {
    DateLastOfficerUpdate: ['Assigned Staff Rep', 'Admin'],
    LastElection: ['Assigned Staff Rep', 'Admin'],
    IUMonthYearNextElection: ['Assigned Staff Rep', 'Admin'],
    LengthOfTerm: ['Assigned Staff Rep', 'Admin'],
  },

  // Local President
  localPresident: {
    PRESIDENT_FIRST: ['Assigned Staff Rep', 'Admin'],
    PRESIDENT_LAST: ['Assigned Staff Rep', 'Admin'],
    PRESIDENT_EMAIL: ['Assigned Staff Rep', 'Admin'],
    PRESIDENT_PRIMARY_PHONE: ['Assigned Staff Rep', 'Admin'],
    PRESIDENT_SECONDARY_PHONE: ['Assigned Staff Rep', 'Admin'],
  },

  // Local Vice President
  localVicePresident: {
    VICE_PRESIDENT_FIRST: ['Assigned Staff Rep', 'Admin'],
    VICE_PRESIDENT_LAST: ['Assigned Staff Rep', 'Admin'],
    VICE_PRESIDENT_EMAIL: ['Assigned Staff Rep', 'Admin'],
    VICE_PRESIDENT_PRIMARY_PHONE: ['Assigned Staff Rep', 'Admin'],
    VICE_PRESIDENT_SECONDARY_PHONE: ['Assigned Staff Rep', 'Admin'],
  },

  // Local Treasurer
  localTreasurer: {
    TREASURER_FIRST: ['Assigned Staff Rep', 'Admin'],
    TREASURER_LAST: ['Assigned Staff Rep', 'Admin'],
    TREASURER_EMAIL: ['Assigned Staff Rep', 'Admin'],
    TREASURER_PRIMARY_PHONE: ['Assigned Staff Rep', 'Admin'],
    TREASURER_SECONDARY_PHONE: ['Assigned Staff Rep', 'Admin'],
  },

  // Local Secretary
  localSecretary: {
    SECRETARY_FIRST: ['Assigned Staff Rep', 'Admin'],
    SECRETARY_LAST: ['Assigned Staff Rep', 'Admin'],
    SECRETARY_EMAIL: ['Assigned Staff Rep', 'Admin'],
    SECRETARY_PRIMARY_PHONE: ['Assigned Staff Rep', 'Admin'],
    SECRETARY_SECONDARY_PHONE: ['Assigned Staff Rep', 'Admin'],
  },
};

// Contract-date editors: specific users granted edit on contract start/end dates
// (CBA_START/CBA_EXP) for ALL units. Added for Monica McCalla at Kelly Martinez's
// request (2026-10). Scoped to these two fields only; matched case-insensitively so
// it works regardless of stored username casing. Keep in sync with the server copy.
const CONTRACT_DATE_FIELDS = ['CBA_START', 'CBA_EXP'];
const CONTRACT_DATE_EDITORS = ['mmccalla'];

// Helper function to check if a user can edit a field
export const canUserEditField = (fieldName, userRole, userName, assignedStaffRep, userFullName, assignedUsers = {}, isCoordinator = false, isAssignedOverride = false) => {
  // Admin, Manager, and Maintainer can edit everything (case-insensitive)
  const normalizedRole = userRole ? userRole.toLowerCase() : '';
  if (normalizedRole === 'admin' || normalizedRole === 'manager' || normalizedRole === 'maintainer') return true;

  // Membership Coordinators can edit ANY unit (the server enforces this too).
  if (isCoordinator) return true;

  // Designated contract-date editors (see note above): edit CBA_START/CBA_EXP on any unit.
  if (CONTRACT_DATE_FIELDS.includes(fieldName) && CONTRACT_DATE_EDITORS.includes(String(userName || '').toLowerCase())) {
    return true;
  }

  // Research Department users (mbaran, nsewell, and Monica McCalla) can only edit specific sections
  if (userName === 'mbaran' || userName === 'nsewell' || userName === 'MMcCalla') {
    // Check if the field belongs to allowed sections for Research Department
    const researchDeptAllowedSections = [
      'payrollHRContacts',
      'contractNegotiations',
      'membershipMetrics',
      'auditsCompliance',
      'financialMetrics',
      'electionOfficerInfo'
    ];
    
    // Find which section the field belongs to
    for (const [sectionName, section] of Object.entries(fieldPermissions)) {
      if (section[fieldName] && researchDeptAllowedSections.includes(sectionName)) {
        return true;
      }
    }
    return false;
  }

  // Check if user is assigned to this unit in ANY capacity. The override comes
  // from an authoritative EmployeeId match (see /api/me/employee) — name-string
  // matching alone breaks when the login name differs from the Employees record.
  const isAssignedToUnit = Boolean(isAssignedOverride) || Object.values(assignedUsers).some(assignedUser =>
    assignedUser && (assignedUser === userName || assignedUser === userFullName)
  );

  // Check if user has Membership role (case-insensitive)
  const hasMembershipRole = normalizedRole === 'membership';

  // Find which section the field belongs to
  for (const section of Object.values(fieldPermissions)) {
    if (section[fieldName]) {
      const allowedRoles = section[fieldName];
      
      // Check if user's name is specifically allowed
      if (allowedRoles.includes(userName)) {
        return true;
      }
      
      // Check if field is restricted to "Membership" role
      if (allowedRoles.includes('Membership') && hasMembershipRole) {
        return true;
      }
      
      // Check if field is restricted to "Assigned Staff Rep"
      // IMPORTANT: Only allow if user is actually assigned to this unit
      if (allowedRoles.includes('Assigned Staff Rep') && isAssignedToUnit) {
        return true;
      }
      
      // Check if user's role (Admin/Manager/Maintainer) is in the allowed roles
      // Note: We already checked for these roles above, so this is redundant
      // but keeping for completeness
      if (allowedRoles.includes('Admin') && normalizedRole === 'admin') {
        return true;
      }
      
      break;
    }
  }
  
  return false;
};

// Helper function specifically for unit-management page - allows all users to edit
export const canUserEditFieldInUnitManagement = (fieldName, userRole, userName) => {
  // All authenticated users can edit in the unit-management page
  // Basic validation - make sure we have a username (user is authenticated)
  if (!userName) {
    return false;
  }
  
  return true;
};

// Get all fields a user can edit (for regular unit info pages)
export const getEditableFields = (userRole, userName, assignedStaffRep, userFullName, assignedUsers = {}, isCoordinator = false, isAssignedOverride = false) => {
  const editableFields = [];

  for (const section of Object.values(fieldPermissions)) {
    for (const [fieldName] of Object.entries(section)) {
      if (canUserEditField(fieldName, userRole, userName, assignedStaffRep, userFullName, assignedUsers, isCoordinator, isAssignedOverride)) {
        editableFields.push(fieldName);
      }
    }
  }

  return editableFields;
};

// Get all fields a user can edit for unit-management page
export const getEditableFieldsForUnitManagement = (userRole, userName) => {
  if (!userName) {
    return []; // No username means not authenticated
  }
  
  const editableFields = [];
  
  for (const section of Object.values(fieldPermissions)) {
    for (const [fieldName] of Object.entries(section)) {
      editableFields.push(fieldName);
    }
  }
  
  return editableFields;
};
